<template>
    <main-page>
        <b-card v-if="user" class="card-container">
            <b-tabs v-model="mainTab">
                <b-tab :title="'Information|Informations'|tr">
                    <h4 class="my-4">
                        <span v-tr>Your Account|Mon compte</span>
                    </h4>

                    <b-form-group class="mx-auto mb-2">
                        <b-container>
                            <b-row>
                                <b-col cols="12" sm="4" class="text-center">
                                    <div class="avatar-picker" @click="$refs.avatar_input.$el.childNodes[0].click()">
                                        <img class="rounded-circle img-thumbnail d-inline avatar-image" :src="avatar_preview">
                                        <b-form-file v-model="user.avatar_file" type="file" ref="avatar_input" accept="image/*" hidden @change="onChange"></b-form-file>
                                        <i style="position: absolute;bottom: 5px;right: 0;font-size: 20px;opacity: 0.5;" class="fas fa-camera"></i>
                                    </div>
                                </b-col>
                                <b-col class="m-auto" cols="12" sm="8">
                                    <div class="f-500 text-gray text-uppercase f-9 text-spaced">{{user.email}}</div>
                                    <b-btn class="btn-personal-info my-2" variant="danger" v-b-modal.modal-password-modification v-tr>Change password|Modifier mon mot de passe</b-btn>
                                </b-col>
                            </b-row>
                        </b-container>
                    </b-form-group>

                    <b-modal @hidden="clearPasswordUpdateModal()" id="modal-password-modification" size="md" :title="'Change password|Changer mon mot de passe'|tr">
                        <template slot="default">
                            <b-form-group :label="'Old password|Ancien mot de passe'|tr">
                                <b-form-input :state="isOldPasswordValid" v-model="passwordUpdate.old_password" type="password" aria-describedby="old-password" maxlength="250"></b-form-input>
                                <b-form-invalid-feedback id="old-password" v-tr>Please enter your old password|Merci d'entrer votre ancien mot de passe</b-form-invalid-feedback>
                            </b-form-group>
                            <b-form-group :label="'New password|Nouveau mot de passe'|tr">
                                <b-form-input :state="isNewPasswordValid" v-model="passwordUpdate.new_password" type="password" aria-describedby="new-password" maxlength="250"></b-form-input>
                                <b-form-invalid-feedback id="new-password" v-tr>Your password must have at least 8 characters, a number and an uppercase letter|Votre mot de passe doit avoir au moins 8 caractères, un chiffre et une lettre majuscule.</b-form-invalid-feedback>
                            </b-form-group>
                            <b-form-group :label="'Confirm new password|Confirmer le mot de passe'|tr">
                                <b-form-input :state="isConfirmPasswordValid" v-model="passwordUpdate.confirm" type="password" aria-describedby="confirm-password" maxlength="250"></b-form-input>
                                <b-form-invalid-feedback id="confirm-password" v-tr>Oops, passwords don't match|La confirmation ne correspond pas au mot de passe</b-form-invalid-feedback>
                            </b-form-group>
                        </template>

                        <template slot="modal-footer" slot-scope="{ ok, cancel }">
                            <b-btn variant="secondary" @click="cancel()" v-tr>Cancel|Annuler</b-btn>
                            <b-btn :disabled="!isPasswordUpdateModalValid" variant="blue" @click="ok(), updatePassword()" v-tr>Update password|Modifier le mot de passe</b-btn>
                        </template>
                    </b-modal>

                    <b-row>
                        <b-col cols="12" md="6">
                            <b-form-group :label="'First name|Prénom'|tr">
                                <e-input v-model="user.first_name" type="name"></e-input>
                            </b-form-group>

                            <b-form-group :label="'Last name|Nom'|tr">
                                <e-input v-model="user.last_name" type="name"></e-input>
                            </b-form-group>

                            <b-form-group :label="'Birth Date|Date de naissance'|tr">
                                <e-input-birthday v-model="user.birth_date"/>
                            </b-form-group>
                        </b-col>
                        <b-col sm="12" md="6">
                            <b-form-group :label="'Preferred Language|Langue'|tr">
                                <b-form-select v-model="user.language">
                                    <option value="en" v-tr>English|Anglais</option>
                                    <option value="fr" v-tr>French|Français</option>
                                </b-form-select>
                            </b-form-group>

                            <b-form-group :label="'Gender|Genre'|tr">
                                <b-form-select v-model="user.gender">
                                    <option value="" v-tr>Non specified|Non spécifié</option>
                                    <option value="male" v-tr>Man|Homme</option>
                                    <option value="female" v-tr>Woman|Femme</option>
                                    <option value="other" v-tr>Other|Autre</option>
                                </b-form-select>
                            </b-form-group>

                            <b-form-group :label="'Postal Code|Code postal'|tr">
                                <e-input v-model="user.postal_code" type="postal-code" maxlength="10"></e-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-form-group :label="'Phone number|Numéro de téléphone'|tr">
                        <vue-phone-input class="input-phone" v-model="phoneNumber.value" @update="user.phone_number = $event.e164, phoneNumber.isValid = $event.isValid" v-if="isFrench"
                                         :translations="{
                                                         countrySelectorLabel: 'Code du pays',
                                                         countrySelectorError: 'Choisir un pays',
                                                         phoneNumberLabel: 'Numéro de téléphone',
                                                         example: 'Exemple :'
                                                     }"/>
                        <vue-phone-input class="input-phone" v-model="phoneNumber.value" @update="user.phone_number = $event.e164, phoneNumber.isValid = $event.isValid" v-else/>
                    </b-form-group>
                </b-tab>
                <b-tab :title="'Preferences|Préférences'|tr">
                    <h4 class="my-4">
                        <span v-tr>Advertising Preferences|Préférences publicitaires</span>
                    </h4>
                    <b-form-group :label="'Email|Courriel'|tr">
                        <b-checkbox switch v-model="user.use_email">
                            {{user.email}}
                        </b-checkbox>
                        <small v-tr v-if="user.use_email">Allow to send communications by email |Autoriser à envoyer
                            des communications publicitaires par courriel
                        </small>
                        <small v-tr v-else>Not allowed to communicate by email|Non autorisé à communiquer par courriel
                        </small>
                    </b-form-group>
                    <b-form-group :label="'Gender|Genre'|tr">
                        <b-checkbox switch v-model="user.use_gender">
                            <span v-if="user.gender === 'male'" v-tr>Man|Homme</span>
                            <span v-else-if="user.gender === 'female'" v-tr>Woman|Femme</span>
                            <span v-else v-tr>Unknown|Inconnu</span>
                        </b-checkbox>
                        <small v-tr v-if="user.use_gender">Allow to use gender to provide more relevant
                            content|Autoriser à utiliser votre genre pour afficher des annonces plus pertinentes
                        </small>
                        <small v-tr v-else>Not allowed to use gender|Non autorisé à utiliser votre genre</small>
                    </b-form-group>
                    <b-form-group :label="'Age Range|Tranche d\'âge'|tr">
                        <b-checkbox switch v-model="user.use_age">
                            {{ageRange}}
                        </b-checkbox>
                        <small v-tr v-if="user.use_age">Allow to use age range to provide more relevant
                            content|Autoriser à utiliser la tranche d'âge pour afficher des annonces plus pertinentes
                        </small>
                        <small v-tr v-else>Not allowed to use age range|Non autorisé à utiliser votre tranche d'âge</small>
                    </b-form-group>
                    <b-form-group :label="'Birthday|Anniversaire'|tr">
                        <b-checkbox switch v-model="user.use_birthday">
                            {{user.birth_date|date}}
                        </b-checkbox>
                        <small v-tr v-if="user.use_birthday">Allow to use this date to offer you special content for
                            your birthday|Autoriser à utiliser cette date pour vous offrir du contenu spécial pour votre
                            anniversaire
                        </small>
                        <small v-tr v-else>Not allowed to use birthday|Non autorisé à utiliser votre date d'anniversaire
                        </small>
                    </b-form-group>
                </b-tab>
                <b-tab :title="'Social Networks|Réseaux sociaux'|tr">
                    <h4 class="my-4">
                        <span v-tr>Manage Social Networks Applications|Gérer ses applications</span>
                    </h4>
                    <b-form-group :label="'Facebook Settings|Paramètres Facebook'|tr">
                        <a class="text-facebook" target="_blank" href="https://www.facebook.com/settings?tab=applications">
                            <i class="fab fa-facebook mr-1"></i>
                            <small v-tr>Applications Settings|Paramètres des applications</small>
                        </a>
                    </b-form-group>
                </b-tab>
            </b-tabs>

            <div slot="footer" class="text-right">
                <b-btn size="sm" class="mr-2" variant="light" to="/"><span v-tr>Cancel|Annuler</span></b-btn>
                <b-btn size="sm" variant="primary" @click="savePreferences"><span v-tr>Save Preferences|Enregistrer</span>
                </b-btn>
            </div>
        </b-card>
    </main-page>
</template>

<script>
import {isFrench} from "../../vue-components/helpers/i18n.js";
import Network from '../../vue-components/helpers/Network'
import Validation from "../../vue-components/helpers/Validation.js";

    export default {
        name: `home`,
        data() {
            return {
                Validation: Validation,
                mainTab: 0,
                avatars: [],
                guestEmail: ``,
                invitations: [],
                sponsored: [],
                passwordUpdate: {
                    old_password: ``,
                    new_password: ``,
                    confirm: ``
                },
                avatar_temp_image: null,
                invitationsLoading: false,
                phoneNumber: {
                    value: ``,
                    isValid: true
                },
                isFrench: isFrench(),
                ageRanges: [18, 25, 35, 45, 55, 65]
            };
        },
        computed: {
            avatar_preview() {
                if (!this.$store.state.user)
                    return null;
                else if (this.avatar_temp_image)
                    return this.avatar_temp_image;
                else if (this.$store.state.user.avatar_image)
                    return this.$store.state.user.avatar_image + `?time=` + new Date().getTime();
                else
                    return require(`../assets/img/avatars/default.png`);
            },
            age() {
                if (this.user && this.user.birth_date) {
                    return this.$moment().year() - this.$moment(this.user.birth_date).year();
                } else {
                    return ``;
                }
            },
            ageRange() {
                if (this.age) {
                    for (let i = 0; i < this.ageRanges.length; i++) {
                        const range = this.ageRanges[i];
                        if (this.age < range) {
                            if (i === 0) {
                                return `18-`;
                            } else {
                                return `${this.ageRanges[i - 1]}-${range}`;
                            }
                        }
                    }
                    return `65+`;
                } else {
                    return ``;
                }
            },
            isOldPasswordValid() {
                return this.passwordUpdate.old_password !== ``;
            },
            isNewPasswordValid() {
                return Validation.testPassword(this.passwordUpdate.new_password);
            },
            isConfirmPasswordValid() {
                return this.isNewPasswordValid && this.passwordUpdate.new_password === this.passwordUpdate.confirm;
            },
            isPasswordUpdateModalValid() {
                return this.isOldPasswordValid && this.isNewPasswordValid && this.isConfirmPasswordValid;
            }
        },
        methods: {
            savePreferences() {
                Network.post(`/wifi/client/user/update`, this.user)
                    .then(() => {
                        this.$swal({
                            icon: `success`,
                            title: this.tr([`Success`, `Succès`]),
                            text: this.tr([`Your preferences have been successfully updated`, `Vos préférences ont été enregistrées`])
                        }).then(() => {
                        });
                        this.$router.push(`/`);
                    })
                    .catch(resp => {
                        this.$swal({
                            icon: `error`,
                            title: this.tr([`Error`, `Erreur`]),
                            text: this.tr([`Something wrong happened`, `Une erreur inconnue s'est produite`])
                        });
                    });
            },
            updatePassword() {
                Network.post(`/wifi/client/user/update-password`, {
                    id: this.user.id,
                    old_password: this.passwordUpdate.old_password,
                    new_password: this.passwordUpdate.new_password
                }).then(resp => {
                    this.$swal({
                        icon: `success`,
                        title: this.tr([`Success`, `Succès`]),
                        text: this.tr([`Password has been successfully updated`, `Mot de passe mis à jour`])
                    }).then(() => {
                        this.$emit(`profile-saved`);
                    });
                }).catch(resp => {
                    this.$swal({
                        icon: `error`,
                        title: this.tr([`Error`, `Erreur`]),
                        text: this.tr([`Error updating password`, `Erreur lors de la mise à jour du mot de passe`])
                    });
                });
            },
            clearPasswordUpdateModal() {
                this.passwordUpdate.old_password = ``;
                this.passwordUpdate.new_password = ``;
                this.passwordUpdate.confirm = ``;
            },
            onChange(e) {
                let files = e.target.files;
                if (files.length > 0) {
                    this.createFile(files[0]);
                    this.$emit(`input`, files[0]);
                } else {
                    this.$emit(`input`, null);
                }
            },
            createFile(file) {
                if (!file.type.match(`image.*`)) {
                    alert(`Select an image`);
                    return;
                }
                let reader = new FileReader();
                let vm = this;

                reader.onload = function(e) {
                    vm.avatar_temp_image = e.target.result;
                };
                reader.readAsDataURL(file);
            }
        }
    }
</script>

<style lang="scss" scoped>
    .card {
        width: 600px;
        max-width: 100%;

        .custom-switch {
            user-select: none;
        }

        small {
            color: #888;
        }
    }

    .avatar-picker {
        max-width: 110px;
        margin: 0 auto;
        position: relative;

        .b-form-file {
            display: none;
        }
    }

    .img-avatar:hover {
        cursor: pointer;
    }

    .btn-personal-info {
        font-size: 8pt !important;
    }

    .avatar-image {
        max-width: 90px;
        width: 90px;
        max-height: 90px;
        height: 90px;
        object-fit: cover;
        object-position: center right;
    }
</style>
